<template>
  <div class="min-h-screen bg-theme-bg py-8 px-4">
    <div class="max-w-5xl mx-auto space-y-6">
      <!-- Loading and Error States -->
      <div v-if="isLoading" class="text-center py-12">
        <p class="text-lg text-theme-text2">Loading your profile...</p>
      </div>

      <div v-else-if="!isAuthenticated" class="text-center py-12">
        <p class="text-lg text-theme-text2">Please log in to view your profile.</p>
        <button
          @click="router.push('/login')"
          class="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
        >
          Log In
        </button>
      </div>

      <div v-else-if="errorMessage" class="text-center py-12">
        <p class="text-lg text-red-600">{{ errorMessage }}</p>
        <button
          @click="retryLoad"
          class="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
        >
          Retry
        </button>
      </div>

      <!-- MAIN CONTENT -->
      <div v-else class="space-y-6">
        <!-- Header -->
        <header
          class="bg-gradient-to-r from-primary-400 to-primary-600 text-white rounded-xl shadow-lg p-6 text-center relative overflow-hidden"
        >
          <div class="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-white/10 blur-2xl"></div>
          <h1 class="text-3xl font-bold tracking-tight">Your Account Dashboard</h1>
          <p class="mt-1 text-sm text-white/80">
            Complete your profile to unlock renting and listing.
          </p>
        </header>

        <div class="grid gap-6 lg:grid-cols-[3fr,1fr]">
          <!-- LEFT: Profile Details -->
          <div class="space-y-6">
            <!-- Profile Card -->
            <section
              class="bg-theme-card rounded-xl shadow-sm border border-theme-border p-6 flex flex-col sm:flex-row items-start gap-4 relative"
            >
              <div class="relative flex-shrink-0">
                <img
                  v-if="authStore.user?.image"
                  :src="authStore.user.image"
                  :alt="userInitials"
                  class="w-24 h-24 rounded-lg border-2 border-theme-border object-cover"
                />
                <div
                  v-else
                  class="w-24 h-24 rounded-lg border-2 border-theme-border bg-primary-600 text-white flex items-center justify-center text-2xl font-bold select-none"
                >{{ userInitials }}</div>
                <button
                  @click="openImageModal"
                  class="absolute -bottom-2 -right-2 bg-primary-600 text-white rounded-full p-1.5 hover:bg-primary-700 transition-colors"
                  title="Change profile picture"
                  aria-label="Change profile picture"
                >
                  <i class="pi pi-camera text-lg"></i>
                </button>
              </div>

              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <h2 class="text-lg font-semibold text-theme-text truncate">
                    {{ authStore.user?.first_name || 'Unknown' }}
                    {{ authStore.user?.last_name || '' }}
                  </h2>
                  <span
                    class="px-2 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium"
                  >
                    {{ authStore.user?.role || 'Role N/A' }}
                  </span>
                </div>
                <p class="mt-1 text-sm text-primary-600">
                  {{ authStore.user?.email || 'No email provided' }}
                </p>
              </div>

              <div class="flex items-center gap-2">
                <span class="text-sm text-theme-text2">Switch role</span>
                <label class="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="authStore.isOn"
                    class="sr-only peer"
                    @change="authStore.updateUserRole()"
                    aria-label="Switch role"
                    :disabled="!authStore.user"
                  />
                  <div
                    class="w-10 h-5 bg-theme-border rounded-full peer-checked:bg-primary-600 peer-checked:after:translate-x-5
                           after:content-[''] after:absolute after:left-0.5 after:top-0.5 after:w-4 after:h-4 after:rounded-full after:bg-white transition-all"
                  ></div>
                </label>
              </div>
            </section>

            <!-- Info Grid -->
            <section class="grid gap-4 grid-cols-2 auto-rows-max w-full min-w-0">
              <!-- Email (read-only) -->
              <div class="bg-theme-card rounded-xl shadow-sm border border-theme-border p-6 flex flex-col min-w-0 overflow-hidden">
                <p class="text-xs font-medium text-theme-muted uppercase">Email</p>
                <p class="mt-2 text-base font-medium text-theme-text flex-1 leading-relaxed break-all">
                  {{ authStore.user?.email || 'Not provided' }}
                </p>
              </div>

              <!-- Phone -->
              <div class="bg-theme-card rounded-xl shadow-sm border border-theme-border p-6 flex flex-col">
                <p class="text-xs font-medium text-theme-muted uppercase">Phone</p>
                <p class="mt-2 text-base font-medium text-theme-text flex-1 leading-relaxed">
                  {{ authStore.user?.phone_number || 'Not provided' }}
                </p>
                <button
                  @click="openPhoneModal"
                  class="mt-3 text-primary-600 hover:text-primary-600 text-sm flex items-center gap-1"
                >
                  <i class="pi pi-pencil text-xs"></i>
                  {{ authStore.user?.phone_number ? 'Edit' : 'Add' }} Phone
                </button>
              </div>

              <!-- PayPal -->
              <div class="bg-theme-card rounded-xl shadow-sm border border-theme-border p-6 flex flex-col min-w-0 overflow-hidden">
                <p class="text-xs font-medium text-theme-muted uppercase">PayPal Email</p>
                <p class="mt-2 text-base font-medium text-theme-text flex-1 leading-relaxed break-all">
                  {{ authStore.user?.paypal_email || 'Not provided' }}
                </p>
                <button
                  @click="openPaypalModal"
                  class="mt-3 text-primary-600 hover:text-primary-600 text-sm flex items-center gap-1"
                >
                  <i class="pi pi-pencil text-xs"></i>
                  {{ authStore.user?.paypal_email ? 'Edit' : 'Add' }} PayPal
                </button>
              </div>

              <!-- Address (no company) -->
              <div class="bg-theme-card rounded-xl shadow-sm border border-theme-border p-6 flex flex-col">
                <p class="text-xs font-medium text-theme-muted uppercase">Address</p>
                <p class="mt-2 text-base font-medium text-theme-text break-words flex-1 leading-relaxed whitespace-normal">
                  {{ humanReadableAddress || 'Not provided' }}
                </p>
                <button
                  @click="openAddressModal"
                  class="mt-3 text-primary-600 hover:text-primary-600 text-sm flex items-center gap-1"
                >
                  <i class="pi pi-pencil text-xs"></i>
                  {{ authStore.user?.user_address ? 'Edit' : 'Add' }} Address
                </button>
              </div>

              <!-- Street -->
              <div class="bg-theme-card rounded-xl shadow-sm border border-theme-border p-6 flex flex-col">
                <p class="text-xs font-medium text-theme-muted uppercase">Street</p>
                <p class="mt-2 text-base font-medium text-theme-text break-words flex-1 leading-relaxed whitespace-normal">
                  {{ authStore.user?.user_address?.street_address || 'Not provided' }}
                </p>
              </div>

              <!-- City -->
              <div class="bg-theme-card rounded-xl shadow-sm border border-theme-border p-6 flex flex-col">
                <p class="text-xs font-medium text-theme-muted uppercase">City</p>
                <p class="mt-2 text-base font-medium text-theme-text break-words flex-1 leading-relaxed whitespace-normal">
                  {{ authStore.user?.user_address?.city || 'Not provided' }}
                </p>
              </div>

              <!-- Zip -->
              <div class="bg-theme-card rounded-xl shadow-sm border border-theme-border p-6 flex flex-col">
                <p class="text-xs font-medium text-theme-muted uppercase">Zip Code</p>
                <p class="mt-2 text-base font-medium text-theme-text flex-1 leading-relaxed">
                  {{ authStore.user?.user_address?.zip_code || 'Not provided' }}
                </p>
              </div>

              <!-- Country -->
              <div class="bg-theme-card rounded-xl shadow-sm border border-theme-border p-6 flex flex-col">
                <p class="text-xs font-medium text-theme-muted uppercase">Country</p>
                <p class="mt-2 text-base font-medium text-theme-text flex-1 leading-relaxed">
                  {{ authStore.user?.user_address?.country || 'Not provided' }}
                </p>
              </div>

              <!-- Identity -->
              <div class="bg-theme-card rounded-xl shadow-sm border border-theme-border p-6 flex flex-col">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <p class="text-xs font-medium text-theme-muted uppercase">
                      Identity Verification
                    </p>
                    <p class="mt-2 text-base font-medium text-theme-text flex items-center gap-2">
                      <i
                        v-if="hasIdentityVerified"
                        class="pi pi-shield text-primary-600 text-primary-600"
                      ></i>
                      {{ hasIdentityVerified ? 'Verified' : 'Not provided' }}
                    </p>
                  </div>
                  <button
                    v-if="!hasIdentityVerified"
                    @click="openIdentityModal"
                    class="text-primary-600 hover:text-primary-600 text-sm flex items-center gap-1"
                  >
                    <i class="pi pi-plus-circle text-xs"></i>Add
                  </button>
                </div>
              </div>

              <!-- Proof of Address -->
              <div class="bg-theme-card rounded-xl shadow-sm border border-theme-border p-6 flex flex-col">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <p class="text-xs font-medium text-theme-muted uppercase">
                      Proof of Address
                    </p>
                    <p class="mt-2 text-base font-medium text-theme-text">
                      {{ authStore.user?.proof_of_address ? 'Provided' : 'Not provided' }}
                    </p>
                  </div>
                  <button
                    @click="openProofModal"
                    class="text-primary-600 hover:text-primary-600 text-sm flex items-center gap-1"
                  >
                    <i class="pi pi-pencil text-xs"></i>
                    {{ authStore.user?.proof_of_address ? 'Update' : 'Add' }}
                  </button>
                </div>
              </div>
            </section>

            <!-- Your Location (Google Map) -->
            <section class="bg-theme-card rounded-xl shadow-sm border border-theme-border p-5 space-y-3">
              <div class="flex items-center justify-between gap-2">
                <div>
                  <p class="text-xs font-medium text-theme-muted uppercase">
                    Your Location
                  </p>
                  <p class="mt-1 text-sm text-theme-text">
                    {{
                      humanReadableAddress ||
                      'Add your address to see it on the map and improve distance estimates.'
                    }}
                  </p>
                </div>
                <button
                  v-if="!hasAddress"
                  @click="openAddressModal"
                  class="text-primary-600 hover:text-primary-600 text-xs font-semibold flex items-center gap-1"
                >
                  <i class="pi pi-pencil text-xs"></i>
                  Add Address
                </button>
              </div>

              <div
                v-if="hasAddress && mapsEmbedUrl"
                class="mt-3 rounded-xl overflow-hidden border border-theme-border"
              >
                <iframe
                  :src="mapsEmbedUrl"
                  width="100%"
                  height="220"
                  style="border:0;"
                  loading="lazy"
                  referrerpolicy="no-referrer-when-downgrade"
                  allowfullscreen
                ></iframe>
              </div>
              <p v-else class="mt-3 text-xs text-theme-muted">
                Once you add your address, we’ll show it on the map and use it to
                calculate how far equipment items are from you.
              </p>
            </section>
          </div>

          <!-- RIGHT: Profile Completion + Guided Flow -->
          <section class="bg-theme-card rounded-xl shadow-sm border border-theme-border p-6 sticky top-4 h-max">
            <h2 class="text-lg font-semibold text-theme-text mb-4">Profile Completion</h2>
            <div class="flex items-center gap-4 mb-4">
              <div class="relative w-24 h-24">
                <svg class="w-full h-full" viewBox="0 0 36 36">
                  <path
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="var(--color-border, #1c1c1c10)"
                    stroke-width="3"
                  />
                  <path
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    :stroke="profileStats.percentage >= 90 ? '#10b981' : 'var(--color-primary-600, #ff6f00)'"
                    stroke-width="3"
                    :stroke-dasharray="`${profileStats.percentage} 100`"
                    class="transition-all duration-500"
                  />
                </svg>
                <div class="absolute inset-0 flex items-center justify-center">
                  <span class="text-lg font-bold text-theme-text">
                    {{ profileStats.percentage }}%
                  </span>
                </div>
              </div>
              <div>
                <p class="text-sm text-theme-text2">
                  Role:
                  <span class="font-medium text-primary-600">
                    {{ authStore.user?.role || 'Not assigned' }}
                  </span>
                </p>
                <p class="text-xs text-theme-muted mt-1">
                  Required: {{ profileStats.requiredScore }}%
                </p>
                <p class="text-xs text-theme-muted">
                  Optional: {{ profileStats.optionalScore }}%
                </p>
              </div>
            </div>

            <!-- Ready badges -->
            <div class="space-y-2">
              <div
                v-if="authStore.user?.role === 'lessor'"
                :class="
                  profileStats.canListItems
                    ? 'bg-emerald-500/10 text-emerald-600'
                    : 'bg-primary-500/10 text-primary-600'
                "
                class="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium"
              >
                <i
                  :class="
                    profileStats.canListItems
                      ? 'pi pi-check-circle'
                      : 'pi pi-exclamation-circle'
                  "
                ></i>
                {{ profileStats.canListItems ? 'Ready to list items' : 'Complete ≥85% to list' }}
              </div>

              <div
                v-if="authStore.user?.role === 'lessee'"
                :class="
                  profileStats.canRentItems
                    ? 'bg-emerald-500/10 text-emerald-600'
                    : 'bg-primary-500/10 text-primary-600'
                "
                class="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium"
              >
                <i
                  :class="
                    profileStats.canRentItems
                      ? 'pi pi-check-circle'
                      : 'pi pi-exclamation-circle'
                  "
                ></i>
                {{ profileStats.canRentItems ? 'Ready to rent items' : 'Complete ≥70% to rent' }}
              </div>
            </div>

            <!-- Primary action: start renting / listing -->
            <div class="mt-4 space-y-2">
              <button
                v-if="authStore.user?.role === 'lessor'"
                @click="handleStartListing"
                class="w-full px-4 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2
                       bg-primary-600 text-theme-text hover:bg-primary-700 disabled:opacity-60"
              >
                <i class="pi pi-plus text-xs"></i>
                {{ profileStats.canListItems ? 'List an item now' : 'Get ready to list' }}
              </button>

              <button
                v-if="authStore.user?.role === 'lessee'"
                @click="handleStartRenting"
                class="w-full px-4 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2
                       bg-primary-600 text-theme-text hover:bg-primary-700 disabled:opacity-60"
              >
                <i class="pi pi-shopping-bag text-xs"></i>
                {{ profileStats.canRentItems ? 'Start renting' : 'Get ready to rent' }}
              </button>
            </div>

            <!-- Next guided step -->
            <div
              v-if="nextStep"
              class="mt-4 rounded-lg border border-primary-500/30 bg-primary-500/5 px-3 py-2 text-sm flex items-start gap-3"
            >
              <div class="mt-0.5">
                <i class="pi pi-arrow-right text-primary-600 text-primary-600"></i>
              </div>
              <div class="flex-1">
                <p class="font-medium text-theme-text">
                  Next step to unlock {{ currentGoalLabel }}:
                </p>
                <p class="text-xs text-theme-text2 mt-0.5">
                  {{ nextStep.label }}
                </p>
                <button
                  @click="goToNextStep"
                  class="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:text-primary-700"
                >
                  Complete this now
                  <i class="pi pi-chevron-right text-[10px]"></i>
                </button>
              </div>
            </div>

            <!-- Remaining checklist -->
            <div class="mt-4">
              <p class="text-xs font-semibold text-theme-text2 mb-1">
                Remaining items
                <span v-if="remainingChecklist.length" class="font-normal">
                  ({{ remainingChecklist.length }})
                </span>
              </p>
              <div
                v-if="remainingChecklist.length"
                class="rounded-lg bg-theme-bg2 max-h-44 overflow-auto"
              >
                <ul class="divide-y divide-theme-border text-xs">
                  <li
                    v-for="item in remainingChecklist"
                    :key="item.key"
                    class="flex items-center justify-between gap-2 px-3 py-2"
                  >
                    <div class="flex items-center gap-2">
                      <i
                        :class="
                          item.required
                            ? 'pi pi-exclamation-circle text-primary-600'
                            : 'pi pi-circle text-theme-muted text-theme-muted'
                        "
                      ></i>
                      <span
                        class="text-theme-text"
                        :class="item.required ? 'font-medium' : 'font-normal'"
                      >
                        {{ item.label }}
                        <span
                          v-if="item.required"
                          class="uppercase text-[10px] ml-1 text-primary-600"
                        >
                          required
                        </span>
                      </span>
                    </div>
                    <button
                      class="text-[11px] text-primary-600 hover:text-primary-700 font-semibold"
                      @click="goToStep(item.key)"
                    >
                      Fill
                    </button>
                  </li>
                </ul>
              </div>
              <p
                v-else
                class="text-xs text-theme-muted bg-theme-bg2 rounded-lg px-3 py-2 mt-1"
              >
                All key details are filled. You’re good to go!
              </p>
            </div>
          </section>
        </div>

        <!-- Withdrawal Component (if you re-enable it) -->
        <!-- <Withdrawal v-if="isAuthenticated" class="bg-theme-card rounded-xl shadow-sm border border-theme-border p-6" /> -->
      </div>
    </div>

    <!-- =================== Modals =================== -->

    <!-- Image Modal -->
    <div
      v-if="imageModalVisible"
      class="fixed inset-0 bg-theme-overlay flex items-center justify-center z-50 px-4"
      @keydown.escape="imageModalVisible = false"
    >
      <div
        class="bg-theme-card rounded-xl p-6 w-full max-w-md animate-slide-up"
        role="dialog"
        aria-modal="true"
        aria-label="Update Profile Picture"
      >
        <h3 class="text-lg font-semibold text-theme-text mb-4">
          {{ authStore.user?.image ? 'Update' : 'Add' }} Profile Picture
        </h3>
        <form @submit.prevent="uploadProfilePicture" novalidate>
          <div class="mb-3">
            <label
              for="profile-pic"
              class="block text-sm font-medium text-theme-text2"
              >Choose Image</label
            >
            <input
              id="profile-pic"
              type="file"
              accept="image/jpeg,image/png"
              class="input"
              @change="handleImageSelect"
              required
            />
            <p v-if="imgError" class="text-xs text-red-600 mt-1">{{ imgError }}</p>
          </div>
          <div class="flex justify-end gap-3">
            <button
              type="button"
              class="px-4 py-2 bg-theme-bg2 rounded-lg hover:bg-theme-border"
              @click="imageModalVisible = false"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="!!imgError || !selectedImage || isUpdating"
              class="px-4 py-2 bg-primary-600 text-theme-text rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {{ isUpdating ? 'Saving…' : 'Save' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Phone Modal -->
    <div
      v-if="phoneModalVisible"
      class="fixed inset-0 bg-theme-overlay flex items-center justify-center z-50 px-4"
      @keydown.escape="phoneModalVisible = false"
    >
      <div
        class="bg-theme-card rounded-xl p-6 w-full max-w-md animate-slide-up"
        role="dialog"
        aria-modal="true"
        aria-label="Update Phone"
      >
        <h3 class="text-lg font-semibold text-theme-text mb-4">
          {{ authStore.user?.phone_number ? 'Update' : 'Add' }} Phone Number
        </h3>
        <form @submit.prevent="updatePhone" novalidate>
          <div class="mb-1">
            <label class="block text-sm font-medium text-theme-text2 mb-1.5">Phone Number</label>
            <vue-tel-input
              v-model="phoneRawInput"
              mode="international"
              :inputOptions="{ placeholder: 'Enter your phone number', name: 'phone' }"
              :dropdownOptions="{ showSearchBox: true, showFlags: true, showDialCodeInList: true }"
              @on-input="(num, obj) => { phoneForm.phone_number = obj?.valid ? obj.number : num; phoneIsValid = !!obj?.valid; phoneErrors.phone_number = '' }"
              @validate="(obj) => { phoneIsValid = !!obj?.valid }"
              class="tel-input-account"
            />
          </div>
          <p
            v-if="phoneErrors.phone_number"
            class="text-xs text-red-600 mb-3"
          >
            {{ phoneErrors.phone_number }}
          </p>
          <div class="flex justify-end gap-3 mt-4">
            <button
              type="button"
              class="px-4 py-2 bg-theme-bg2 rounded-lg hover:bg-theme-border"
              @click="phoneModalVisible = false"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="!phoneValid || isUpdating"
              class="px-4 py-2 bg-primary-600 text-theme-text rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {{ isUpdating ? 'Saving…' : 'Save' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- PayPal Modal -->
    <div
      v-if="paypalModalVisible"
      class="fixed inset-0 bg-theme-overlay flex items-center justify-center z-50 px-4"
      @keydown.escape="paypalModalVisible = false"
    >
      <div
        class="bg-theme-card rounded-xl p-6 w-full max-w-md animate-slide-up"
        role="dialog"
        aria-modal="true"
        aria-label="Update PayPal"
      >
        <h3 class="text-lg font-semibold text-theme-text mb-4">
          {{ authStore.user?.paypal_email ? 'Update' : 'Add' }} PayPal Email
        </h3>
        <form @submit.prevent="updatePaypal" novalidate>
          <div class="mb-1">
            <label
              for="paypal_email"
              class="block text-sm font-medium text-theme-text2"
              >PayPal Email</label
            >
            <input
              id="paypal_email"
              v-model.trim="paypalForm.paypal_email"
              type="email"
              :class="inputClass(paypalErrors.paypal_email)"
              placeholder="e.g., user@paypal.com"
              @input="validatePaypal"
              required
            />
          </div>
          <p
            v-if="paypalErrors.paypal_email"
            class="text-xs text-red-600 mb-3"
          >
            {{ paypalErrors.paypal_email }}
          </p>
          <div class="flex justify-end gap-3">
            <button
              type="button"
              class="px-4 py-2 bg-theme-bg2 rounded-lg hover:bg-theme-border"
              @click="paypalModalVisible = false"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="!paypalValid || isUpdating"
              class="px-4 py-2 bg-primary-600 text-theme-text rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {{ isUpdating ? 'Saving…' : 'Save' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Address Modal -->
    <div
      v-if="addressModalVisible"
      class="fixed inset-0 bg-theme-overlay flex items-center justify-center z-50 px-4"
      @keydown.escape="addressModalVisible = false"
    >
      <div
        class="bg-theme-card rounded-xl p-6 w-full max-w-md animate-slide-up"
        role="dialog"
        aria-modal="true"
        aria-label="Update Address"
      >
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-theme-text">
            {{ authStore.user?.user_address?.id ? 'Update' : 'Add' }} Address
          </h3>
          <button
            type="button"
            @click="showAddrMapPicker = true"
            class="flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700 border border-primary-300 hover:border-primary-500 rounded-xl px-3 py-1.5 transition bg-primary-600/5 hover:bg-primary-600/10"
          >
            <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            Pick on map
          </button>
        </div>
        <form @submit.prevent="updateAddress" novalidate>
          <div class="mb-1 relative">
            <label
              for="street"
              class="block text-sm font-medium text-theme-text2"
              >Street Address</label
            >
            <input
              id="street"
              ref="userAddressAutocompleteInput"
              v-model.trim="addressForm.street_address"
              type="text"
              autocomplete="off"
              :class="inputClass(addrErrors.street_address)"
              @input="onAddrInput"
              @blur="setTimeout(() => clearAddrSuggestions(), 200)"
              required
            />
            <!-- Suggestions dropdown -->
            <ul v-if="showAddrSuggestions && addrSuggestions.length"
              class="absolute z-50 left-0 right-0 mt-1 bg-theme-card border border-theme-border rounded-md shadow-lg max-h-48 overflow-y-auto">
              <li v-for="s in addrSuggestions" :key="s.placeId"
                class="px-3 py-2 hover:bg-theme-bg2 cursor-pointer text-sm"
                @mousedown.prevent="selectAddrSuggestion(s)">
                <span class="font-medium text-theme-text">{{ s.main }}</span>
                <span v-if="s.secondary" class="text-theme-muted ml-1">{{ s.secondary }}</span>
              </li>
            </ul>
          </div>
          <p
            v-if="addrErrors.street_address"
            class="text-xs text-red-600 mb-2"
          >
            {{ addrErrors.street_address }}
          </p>

          <!-- Address Line 2 -->
          <div class="mb-1">
            <label
              for="street2"
              class="block text-sm font-medium text-theme-text2"
              >Address Line 2 (optional)</label
            >
            <input
              id="street2"
              v-model.trim="addressForm.street_address2"
              type="text"
              class="input"
            />
          </div>

          <div class="mb-1">
            <label
              for="city"
              class="block text-sm font-medium text-theme-text2"
              >City</label
            >
            <input
              id="city"
              v-model.trim="addressForm.city"
              type="text"
              :class="inputClass(addrErrors.city)"
              @input="validateAddress"
              required
            />
          </div>
          <p v-if="addrErrors.city" class="text-xs text-red-600 mb-2">
            {{ addrErrors.city }}
          </p>

          <!-- State / Region -->
          <div class="mb-1">
            <label
              for="state"
              class="block text-sm font-medium text-theme-text2"
              >State / Region</label
            >
            <input
              id="state"
              v-model.trim="addressForm.state"
              type="text"
              :class="inputClass(addrErrors.state)"
              @input="validateAddress"
              required
            />
          </div>
          <p v-if="addrErrors.state" class="text-xs text-red-600 mb-2">
            {{ addrErrors.state }}
          </p>

          <div class="mb-1">
            <label
              for="zip"
              class="block text-sm font-medium text-theme-text2"
              >Zip Code</label
            >
            <input
              id="zip"
              v-model.trim="addressForm.zip_code"
              type="text"
              :class="inputClass(addrErrors.zip_code)"
              @input="validateAddress"
              required
            />
          </div>
          <p v-if="addrErrors.zip_code" class="text-xs text-red-600 mb-2">
            {{ addrErrors.zip_code }}
          </p>

          <div class="mb-1">
            <label
              for="country"
              class="block text-sm font-medium text-theme-text2"
              >Country</label
            >
            <input
              id="country"
              v-model.trim="addressForm.country"
              type="text"
              :class="inputClass(addrErrors.country)"
              @input="validateAddress"
              required
            />
          </div>
          <p v-if="addrErrors.country" class="text-xs text-red-600 mb-3">
            {{ addrErrors.country }}
          </p>

          <div class="flex justify-end gap-3">
            <button
              type="button"
              class="px-4 py-2 bg-theme-bg2 rounded-lg hover:bg-theme-border"
              @click="addressModalVisible = false"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="!addressValid || isUpdating"
              class="px-4 py-2 bg-primary-600 text-theme-text rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {{ isUpdating ? 'Saving…' : 'Save' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Address Map Picker -->
    <MapPickerModal
      :show="showAddrMapPicker"
      @close="showAddrMapPicker = false"
      @confirm="onAddrMapConfirm"
    />

    <!-- Identity Verification Modal -->
    <div
      v-if="identityModalVisible"
      class="fixed inset-0 bg-theme-overlay flex items-center justify-center z-50 px-4"
      @keydown.escape="identityModalVisible = false"
    >
      <div
        class="bg-theme-card rounded-xl p-6 w-full max-w-md animate-slide-up"
        role="dialog"
        aria-modal="true"
        aria-label="Identity Verification"
      >
        <h3 class="text-lg font-semibold text-theme-text mb-4">
          Complete Identity Verification
        </h3>
        <div
          class="p-3 rounded-lg bg-primary-500/5 text-primary-700 text-sm border border-primary-500/30 mb-4"
        >
          <strong class="font-medium">Note:</strong>
          Double-check your details before submitting — you won’t be able to edit this later.
        </div>
        <form @submit.prevent="saveIdentityVerification" novalidate>
          <div class="mb-1">
            <label
              for="docType"
              class="block text-sm font-medium text-theme-text"
              >Document Type</label
            >
            <select
              id="docType"
              v-model="idvForm.document_type"
              :class="inputClass(idvErrors.document_type)"
              @change="validateIdv"
              required
            >
              <option disabled value="">-- Choose --</option>
              <option value="id">National ID</option>
              <option value="passport">Passport</option>
              <option value="dl">Driver's License</option>
            </select>
          </div>
          <p v-if="idvErrors.document_type" class="text-xs text-red-600 mb-2">
            {{ idvErrors.document_type }}
          </p>

          <div class="mb-1">
            <label
              class="block text-sm font-medium text-theme-text"
              >Identity Document</label
            >
            <input
              type="file"
              accept=".pdf,image/jpeg,image/png"
              @change="handleIdDocSelect"
              :class="inputClass(idvErrors.identity_document)"
              required
            />
            <p class="text-xs text-theme-muted mt-1">PDF/JPG/PNG, up to 10MB</p>
          </div>
          <p v-if="idvErrors.identity_document" class="text-xs text-red-600 mb-2">
            {{ idvErrors.identity_document }}
          </p>

          <div class="flex justify-end gap-3 mt-2">
            <button
              type="button"
              class="px-4 py-2 bg-theme-bg2 rounded-lg hover:bg-theme-border"
              @click="identityModalVisible = false"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="!idvValid || isUpdating"
              class="px-4 py-2 bg-primary-600 text-theme-text rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {{ isUpdating ? 'Submitting…' : 'Submit' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Proof of Address Modal -->
    <div
      v-if="proofModalVisible"
      class="fixed inset-0 bg-theme-overlay flex items-center justify-center z-50 px-4"
      @keydown.escape="proofModalVisible = false"
    >
      <div
        class="bg-theme-card rounded-xl p-6 w-full max-w-md animate-slide-up"
        role="dialog"
        aria-modal="true"
        aria-label="Proof of Address"
      >
        <h3 class="text-lg font-semibold text-theme-text mb-4">
          {{
            authStore.user?.proof_of_address
              ? 'Update Proof of Address'
              : 'Upload Proof of Address'
          }}
        </h3>
        <form @submit.prevent="saveProofOfAddress" novalidate>
          <div class="mb-1">
            <label
              class="block text-sm font-medium text-theme-text"
              >Proof of Address</label
            >
            <input
              type="file"
              accept=".pdf,image/jpeg,image/png"
              @change="handleProofSelect"
              :class="inputClass(proofErrors.file)"
              required
            />
            <p class="text-xs text-theme-muted mt-1">PDF/JPG/PNG, up to 10MB</p>
          </div>
          <p v-if="proofErrors.file" class="text-xs text-red-600 mb-2">
            {{ proofErrors.file }}
          </p>

          <div class="flex justify-end gap-3 mt-2">
            <button
              type="button"
              class="px-4 py-2 bg-theme-bg2 rounded-lg hover:bg-theme-border"
              @click="proofModalVisible = false"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="!proofValid || isUpdating"
              class="px-4 py-2 bg-primary-600 text-theme-text rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {{ isUpdating ? 'Saving…' : 'Save' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import axios from 'axios'
import { useAuthStore } from '../store/auth'
import { useLocationStore } from '../store/location'
import { useRouter } from 'vue-router'
import { useLocationSearch } from '@/composables/useLocationSearch.js'
import MapPickerModal from '@/components/MapPickerModal.vue'
import { useLogger } from '@/composables/useLogger'

const logger = useLogger('Account');

const authStore = useAuthStore()
const locationStore = useLocationStore()
const router = useRouter()
const api_base_url = import.meta.env.VITE_API_BASE_URL || ''
const mapsApiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY || ''

// Loading & error
const isLoading = ref(true)
const errorMessage = ref('')

// User initials for avatar fallback
const userInitials = computed(() => {
  const u = authStore.user
  if (!u) return '?'
  if (u.first_name && u.last_name) return (u.first_name[0] + u.last_name[0]).toUpperCase()
  if (u.first_name) return u.first_name.substring(0, 2).toUpperCase()
  if (u.username) return u.username.substring(0, 2).toUpperCase()
  if (u.email) return u.email.substring(0, 2).toUpperCase()
  return '?'
})

// Auth
const isAuthenticated = computed(() => Boolean(authStore.isAuthenticated))

// UI state (modals)
const imageModalVisible = ref(false)
const phoneModalVisible = ref(false)
const paypalModalVisible = ref(false)
const addressModalVisible = ref(false)
const identityModalVisible = ref(false)
const proofModalVisible = ref(false)

// Busy flags
const isUpdating = ref(false)

// Forms
const phoneForm = ref({ phone_number: '' })
const phoneRawInput = ref('')   // what vue-tel-input displays
const phoneIsValid = ref(false) // set by vue-tel-input validation callback
const paypalForm = ref({ paypal_email: '' })
const addressForm = ref({
  street_address: '',
  street_address2: '',
  city: '',
  state: '',
  zip_code: '',
  country: '',
})
const idvForm = ref({ document_type: '' })

// Files
const selectedImage = ref(null)
const idDocFile = ref(null)
const proofFile = ref(null)

// Validation errors
const phoneErrors = ref({ phone_number: '' })
const paypalErrors = ref({ paypal_email: '' })
const addrErrors = ref({
  street_address: '',
  city: '',
  state: '',
  zip_code: '',
  country: '',
})
const idvErrors = ref({ document_type: '', identity_document: '' })
const proofErrors = ref({ file: '' })
const imgError = ref('')

// Ref for Google Places Autocomplete input
const userAddressAutocompleteInput = ref(null)
const { suggestions: addrSuggestions, showSuggestions: showAddrSuggestions, fetchSuggestions: fetchAddrSuggestions, resolvePlace: resolveAddrPlace, clearSuggestions: clearAddrSuggestions } = useLocationSearch()

// Identity considered verified if type exists
const hasIdentityVerified = computed(() => Boolean(authStore.user?.document_type))

// Helpers
const isFilled = (v) =>
  v !== null &&
  v !== undefined &&
  v !== '' &&
  !(Array.isArray(v) && v.length === 0)

// Role-based requirements
const roleMap = computed(() => {
  const role = authStore.user?.role
  if (role === 'lessor') {
    return {
      required: [
        'first_name',
        'last_name',
        'phone_number',
        'email',
        'paypal_email',
        'document_type',
      ],
      optional: ['proof_of_address', 'image', 'username'],
      thresholds: { list: 85, rent: null },
    }
  }
  if (role === 'lessee') {
    return {
      required: ['first_name', 'last_name', 'phone_number', 'email', 'document_type'],
      optional: ['proof_of_address', 'image', 'username'],
      thresholds: { list: null, rent: 70 },
    }
  }
  return { required: [], optional: [], thresholds: { list: null, rent: null } }
})

// Profile stats
const profileStats = computed(() => {
  const user = authStore.user || {}
  const cfg = roleMap.value
  const requiredFilled = cfg.required.filter((k) => isFieldFilled(user, k)).length
  const optionalFilled = cfg.optional.filter((k) => isFieldFilled(user, k)).length
  const requiredScore = cfg.required.length
    ? Math.round((requiredFilled / cfg.required.length) * 100)
    : 0
  const optionalScore = cfg.optional.length
    ? Math.round((optionalFilled / cfg.optional.length) * 100)
    : 0
  const percentage = Math.round(((requiredScore * 0.8 + optionalScore * 0.2) * 10) / 10)
  const canListItems =
    cfg.thresholds.list !== null ? percentage >= cfg.thresholds.list : false
  const canRentItems =
    cfg.thresholds.rent !== null ? percentage >= cfg.thresholds.rent : false
  return {
    percentage: percentage || 0,
    requiredScore: requiredScore || 0,
    optionalScore: optionalScore || 0,
    canListItems,
    canRentItems,
  }
})

// Helper to read nested fields
function isFieldFilled(user, key) {
  if (key === 'proof_of_address') return isFilled(user?.proof_of_address)
  if (key === 'image') return isFilled(user?.image)
  return isFilled(user?.[key])
}

// Labels for guided steps
const fieldLabels = {
  first_name: 'Add your first name',
  last_name: 'Add your last name',
  email: 'Confirm your email',
  phone_number: 'Add your phone number',
  paypal_email: 'Connect your PayPal email for payouts',
  document_type: 'Verify your identity',
  proof_of_address: 'Upload proof of address',
  image: 'Add a profile picture',
  username: 'Set your username',
}

// priorities for ordering guided steps
const stepPriority = [
  'document_type',
  'phone_number',
  'paypal_email',
  'proof_of_address',
  'image',
  'first_name',
  'last_name',
  'email',
  'username',
]

// Missing required & optional fields for this role
const missingRequiredFields = computed(() => {
  const user = authStore.user || {}
  const cfg = roleMap.value
  const missing = cfg.required.filter((k) => !isFieldFilled(user, k))
  return missing.sort(
    (a, b) => stepPriority.indexOf(a) - stepPriority.indexOf(b)
  )
})

const missingOptionalFields = computed(() => {
  const user = authStore.user || {}
  const cfg = roleMap.value
  const missing = cfg.optional.filter((k) => !isFieldFilled(user, k))
  return missing.sort(
    (a, b) => stepPriority.indexOf(a) - stepPriority.indexOf(b)
  )
})

// Next recommended step (first missing required, then optional)
const nextStep = computed(() => {
  if (missingRequiredFields.value.length) {
    const key = missingRequiredFields.value[0]
    return {
      key,
      required: true,
      label: fieldLabels[key] || 'Complete missing information',
    }
  }
  if (missingOptionalFields.value.length) {
    const key = missingOptionalFields.value[0]
    return {
      key,
      required: false,
      label: fieldLabels[key] || 'Complete missing information',
    }
  }
  return null
})

// Checklist for side panel
const remainingChecklist = computed(() => {
  const requiredItems = missingRequiredFields.value.map((key) => ({
    key,
    required: true,
    label: fieldLabels[key] || 'Complete missing information',
  }))
  const optionalItems = missingOptionalFields.value.map((key) => ({
    key,
    required: false,
    label: fieldLabels[key] || 'Complete missing information',
  }))
  return [...requiredItems, ...optionalItems]
})

// Label depending on role (for "Next step to unlock X")
const currentGoalLabel = computed(() => {
  const role = authStore.user?.role
  if (role === 'lessor') return 'listing your items'
  if (role === 'lessee') return 'renting items'
  return 'your account'
})

// ---- Location / Map (from location store) ----
const hasAddress = computed(() => {
  const addr = locationStore.defaultAddress || locationStore.userAddress
  if (!addr) return false
  const street =
    addr.street_address || addr.street || ''
  const city = addr.city || ''
  const zip = addr.zip_code || addr.zipCode || ''
  const country = addr.country || ''
  return !!(street || city || zip || country)
})

const humanReadableAddress = computed(() => {
  const addr = locationStore.defaultAddress || locationStore.userAddress
  if (!addr) return ''
  const street =
    addr.street_address || addr.street || ''
  const line2 = addr.street_address2 || addr.line2 || ''
  const city = addr.city || ''
  const state = addr.state || ''
  const zip = addr.zip_code || addr.zipCode || ''
  const country = addr.country || ''
  return [street, line2, city, state, zip, country].filter(Boolean).join(', ')
})

const mapsEmbedUrl = computed(() => {
  if (!mapsApiKey || !hasAddress.value) return ''
  // Prefer coordinates from defaultAddress, then from userCoords, then address text
  const addr = locationStore.defaultAddress
  if (addr?.latitude && addr?.longitude) {
    const q = `${Number(addr.latitude)},${Number(addr.longitude)}`
    return `https://www.google.com/maps/embed/v1/place?key=${mapsApiKey}&q=${encodeURIComponent(
      q
    )}`
  }
  if (locationStore.userCoords) {
    const q = `${locationStore.userCoords.lat},${locationStore.userCoords.lng}`
    return `https://www.google.com/maps/embed/v1/place?key=${mapsApiKey}&q=${encodeURIComponent(
      q
    )}`
  }
  if (humanReadableAddress.value) {
    return `https://www.google.com/maps/embed/v1/place?key=${mapsApiKey}&q=${encodeURIComponent(
      humanReadableAddress.value
    )}`
  }
  return ''
})

// Sync auth profile address into location store
const syncUserLocationFromProfile = () => {
  const addr = authStore.user?.user_address
  if (!addr) {
    locationStore.userAddress = {
      street: '',
      line2: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
    }
    locationStore.userCoords = null
    return
  }

  locationStore.userAddress = {
    street: addr.street_address || '',
    line2: addr.street_address2 || '',
    city: addr.city || '',
    state: addr.state || '',
    zipCode: addr.zip_code || '',
    country: addr.country || '',
  }

  if (addr.latitude && addr.longitude) {
    locationStore.userCoords = {
      lat: Number(addr.latitude),
      lng: Number(addr.longitude),
    }
  }
}

// Lifecycle
onMounted(async () => {
  try {
    isLoading.value = true
    errorMessage.value = ''
    await authStore.getUserData()
    syncUserLocationFromProfile()
  } catch (err) {
    logger.error('Failed to fetch user data', err)
    errorMessage.value = 'Failed to load profile. Please try again.'
  } finally {
    isLoading.value = false
  }
})

const retryLoad = async () => {
  try {
    isLoading.value = true
    errorMessage.value = ''
    await authStore.getUserData()
    syncUserLocationFromProfile()
  } catch (err) {
    logger.error('Retry load failed', err)
    errorMessage.value = 'Failed to load profile. Please try again.'
  } finally {
    isLoading.value = false
  }
}

// Merge helper
const mergeUser = (patch) => {
  authStore.user = { ...(authStore.user || {}), ...patch }
}

// Open modals
const openImageModal = () => {
  selectedImage.value = null
  imgError.value = ''
  imageModalVisible.value = true
}
const showAddrMapPicker = ref(false)
const onAddrMapConfirm = (result) => {
  addressForm.value.street_address  = result.street_address  || ''
  addressForm.value.city            = result.city            || ''
  addressForm.value.state           = result.state           || ''
  addressForm.value.zip_code        = result.zip_code        || ''
  addressForm.value.country         = result.country         || ''
  showAddrMapPicker.value = false
}

const openPhoneModal = () => {
  phoneForm.value.phone_number = authStore.user?.phone_number || ''
  phoneRawInput.value = authStore.user?.phone_number || ''
  phoneIsValid.value = !!authStore.user?.phone_number
  phoneErrors.value.phone_number = ''
  phoneModalVisible.value = true
}
const openPaypalModal = () => {
  paypalForm.value.paypal_email = authStore.user?.paypal_email || ''
  paypalErrors.value.paypal_email = ''
  paypalModalVisible.value = true
}
const openAddressModal = () => {
  addressForm.value = {
    street_address: authStore.user?.user_address?.street_address || '',
    street_address2: authStore.user?.user_address?.street_address2 || '',
    city: authStore.user?.user_address?.city || '',
    state: authStore.user?.user_address?.state || '',
    zip_code: authStore.user?.user_address?.zip_code || '',
    country: authStore.user?.user_address?.country || '',
  }
  addrErrors.value = {
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
  }

  // Seed location store with current profile address
  syncUserLocationFromProfile()

  addressModalVisible.value = true
}
const openIdentityModal = () => {
  idvForm.value.document_type = ''
  idDocFile.value = null
  idvErrors.value = { document_type: '', identity_document: '' }
  identityModalVisible.value = true
}
const openProofModal = () => {
  proofFile.value = null
  proofErrors.value.file = ''
  proofModalVisible.value = true
}

// Validation
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const validatePhone = () => {
  phoneErrors.value.phone_number = !phoneForm.value.phone_number
    ? 'Phone number is required.'
    : !phoneIsValid.value
    ? 'Enter a valid international phone number.'
    : ''
}
const phoneValid = computed(
  () => !phoneErrors.value.phone_number && !!phoneForm.value.phone_number && phoneIsValid.value
)

const validatePaypal = () => {
  const v = (paypalForm.value.paypal_email || '').trim()
  paypalErrors.value.paypal_email = !v
    ? 'PayPal email is required.'
    : !emailRegex.test(v)
    ? 'Enter a valid email address.'
    : ''
}
const paypalValid = computed(
  () => !paypalErrors.value.paypal_email && !!paypalForm.value.paypal_email
)

const validateAddress = () => {
  const a = addressForm.value
  addrErrors.value.street_address = a.street_address?.trim()
    ? ''
    : 'Street address is required.'
  addrErrors.value.city = a.city?.trim() ? '' : 'City is required.'
  addrErrors.value.state = a.state?.trim() ? '' : 'State / Region is required.'
  addrErrors.value.zip_code = a.zip_code?.trim() ? '' : 'Zip code is required.'
  addrErrors.value.country = a.country?.trim() ? '' : 'Country is required.'
}
const addressValid = computed(
  () =>
    !addrErrors.value.street_address &&
    !addrErrors.value.city &&
    !addrErrors.value.state &&
    !addrErrors.value.zip_code &&
    !addrErrors.value.country &&
    !!addressForm.value.street_address &&
    !!addressForm.value.city &&
    !!addressForm.value.state &&
    !!addressForm.value.zip_code &&
    !!addressForm.value.country
)

const validateIdv = () => {
  idvErrors.value.document_type = idvForm.value.document_type
    ? ''
    : 'Please select a document type.'
  idvErrors.value.identity_document = idDocFile.value
    ? ''
    : 'Please choose the identity document file.'
}
const idvValid = computed(
  () =>
    !idvErrors.value.document_type &&
    !idvErrors.value.identity_document &&
    !!idvForm.value.document_type &&
    !!idDocFile.value
)

const validateProof = () => {
  proofErrors.value.file = proofFile.value ? '' : 'Please select a file.'
}
const proofValid = computed(
  () => !proofErrors.value.file && !!proofFile.value
)

const inputClass = (err) =>
  `mt-1 w-full rounded-lg border px-3 py-2 text-sm bg-theme-input text-theme-input-text shadow-sm focus:outline-none ${
    err
      ? 'border-red-400 focus:ring-2 focus:ring-red-300'
      : 'border-theme-input-border focus:ring-2 focus:ring-primary-500/60 focus:border-primary-500/60'
  }`

// File handlers
const handleImageSelect = (e) => {
  const f = e.target.files?.[0]
  selectedImage.value = null
  imgError.value = ''
  if (!f) {
    imgError.value = 'Please select an image.'
    return
  }
  if (!['image/jpeg', 'image/png'].includes(f.type)) {
    imgError.value = 'Only JPEG or PNG images are allowed.'
    return
  }
  if (f.size > 5 * 1024 * 1024) {
    imgError.value = 'Image size should be ≤ 5MB.'
    return
  }
  selectedImage.value = f
}
const handleIdDocSelect = (e) => {
  const f = e.target.files?.[0]
  idDocFile.value = null
  idvErrors.value.identity_document = ''
  if (!f) {
    idvErrors.value.identity_document = 'Please choose the identity document file.'
    return
  }
  if (!['application/pdf', 'image/jpeg', 'image/png'].includes(f.type)) {
    idvErrors.value.identity_document = 'Allowed types: PDF, JPG, PNG.'
    return
  }
  if (f.size > 10 * 1024 * 1024) {
    idvErrors.value.identity_document = 'File must be ≤ 10MB.'
    return
  }
  idDocFile.value = f
}
const handleProofSelect = (e) => {
  const f = e.target.files?.[0]
  proofFile.value = null
  proofErrors.value.file = ''
  if (!f) {
    proofErrors.value.file = 'Please select a file.'
    return
  }
  if (!['application/pdf', 'image/jpeg', 'image/png'].includes(f.type)) {
    proofErrors.value.file = 'Allowed types: PDF, JPG, PNG.'
    return
  }
  if (f.size > 10 * 1024 * 1024) {
    proofErrors.value.file = 'File must be ≤ 10MB.'
    return
  }
  proofFile.value = f
}

// ---- ADDRESS AUTOCOMPLETE ----
const onAddrInput = () => {
  fetchAddrSuggestions(addressForm.value.street_address)
  validateAddress()
}

const selectAddrSuggestion = async (suggestion) => {
  clearAddrSuggestions()
  addressForm.value.street_address = suggestion.full
  const result = await resolveAddrPlace(suggestion.placeId, suggestion._prediction)
  if (!result) return

  addressForm.value.street_address = result.street_address || result.formatted || suggestion.full
  addressForm.value.city = result.city || addressForm.value.city
  addressForm.value.state = result.state || addressForm.value.state
  addressForm.value.zip_code = result.zip_code || addressForm.value.zip_code
  addressForm.value.country = result.country || addressForm.value.country

  locationStore.userAddress = {
    street: addressForm.value.street_address,
    line2: '',
    city: result.city,
    state: result.state,
    zipCode: result.zip_code,
    country: result.country,
  }

  if (result.lat != null && result.lng != null) {
    locationStore.userCoords = { lat: result.lat, lng: result.lng }
  }

  validateAddress()
}

// Simple text geocoder (fallback when user doesn't pick suggestion)
const geocodeAddressText = async (addrObj) => {
  const addressText = [
    addrObj.street_address,
    addrObj.street_address2,
    addrObj.city,
    addrObj.state,
    addrObj.zip_code,
    addrObj.country,
  ]
    .filter(Boolean)
    .join(', ')

  if (!mapsApiKey || !addressText) return null

  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
    addressText
  )}&key=${mapsApiKey}`

  try {
    const res = await fetch(url)
    if (!res.ok) {
      logger.error('Geocoding request failed', { status: res.status })
      return null
    }
    const data = await res.json()
    if (data.status !== 'OK' || !data.results?.length) {
      logger.warn('Geocoding returned no results', { status: data.status })
      return {
        lat: null,
        lng: null,
        formatted_address: addressText,
        place_id: '',
        raw_geocode: null,
        full_address: addressText,
      }
    }

    const best = data.results[0]
    return {
      lat: best.geometry.location.lat,
      lng: best.geometry.location.lng,
      formatted_address: best.formatted_address,
      place_id: best.place_id,
      raw_geocode: best,
      full_address: addressText,
    }
  } catch (e) {
    logger.error('Error during geocoding', e)
    return null
  }
}

// Guided actions for a given step key
const goToStep = (key) => {
  switch (key) {
    case 'phone_number':
      openPhoneModal()
      break
    case 'paypal_email':
      openPaypalModal()
      break
    case 'proof_of_address':
      openProofModal()
      break
    case 'document_type':
      openIdentityModal()
      break
    case 'image':
      openImageModal()
      break
    case 'first_name':
    case 'last_name':
    case 'email':
    case 'username':
      router.push('/account/settings')
      break
    default:
      break
  }
}

const goToNextStep = () => {
  if (nextStep.value) {
    goToStep(nextStep.value.key)
  }
}

// Start renting / listing handlers (guided)
const handleStartListing = () => {
  if (profileStats.value.canListItems) {
    router.push('/list-item')
  } else if (nextStep.value) {
    goToNextStep()
  }
}

const handleStartRenting = () => {
  if (profileStats.value.canRentItems) {
    router.push('/categories')
  } else if (nextStep.value) {
    goToNextStep()
  }
}

// Actions (API)
const uploadProfilePicture = async () => {
  if (imgError.value || !selectedImage.value || !authStore.user?.id) {
    alert('Please select a valid image and ensure you are logged in.')
    return
  }
  isUpdating.value = true
  const formData = new FormData()
  formData.append('image', selectedImage.value)
  try {
    const localUrl = URL.createObjectURL(selectedImage.value)
    mergeUser({ image: localUrl })
    const res = await axios.put(
      `${api_base_url}/api/accounts/users/${authStore.user.id}/`,
      formData,
      {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    )
    mergeUser({ image: res.data.image_url || res.data.image || localUrl })
    imageModalVisible.value = false
    await authStore.getUserData()
    syncUserLocationFromProfile()
  } catch (error) {
    logger.error('Error uploading profile picture', error)
    alert(
      error.response?.data?.error ||
        'Failed to upload profile picture. Please try again.'
    )
  } finally {
    isUpdating.value = false
  }
}

const updatePhone = async () => {
  validatePhone()
  if (!phoneValid.value || !authStore.user?.id) {
    alert('Please provide a valid phone number and ensure you are logged in.')
    return
  }
  isUpdating.value = true
  try {
    mergeUser({ phone_number: phoneForm.value.phone_number })
    const res = await axios.put(
      `${api_base_url}/api/accounts/users/${authStore.user.id}/`,
      {
        phone_number: phoneForm.value.phone_number,
      },
      { withCredentials: true }
    )
    mergeUser({
      phone_number: res.data.phone_number || phoneForm.value.phone_number,
    })
    phoneModalVisible.value = false
    await authStore.getUserData()
    syncUserLocationFromProfile()
  } catch (error) {
    logger.error('Error updating phone number', error)
    alert(
      error.response?.data?.error ||
        'Failed to update phone number. Please try again.'
    )
  } finally {
    isUpdating.value = false
  }
}

const updatePaypal = async () => {
  validatePaypal()
  if (!paypalValid.value || !authStore.user?.id) {
    alert('Please provide a valid PayPal email and ensure you are logged in.')
    return
  }
  isUpdating.value = true
  try {
    mergeUser({ paypal_email: paypalForm.value.paypal_email })
    const res = await axios.put(
      `${api_base_url}/api/accounts/users/${authStore.user.id}/`,
      {
        paypal_email: paypalForm.value.paypal_email,
      },
      { withCredentials: true }
    )
    mergeUser({
      paypal_email: res.data.paypal_email || paypalForm.value.paypal_email,
    })
    paypalModalVisible.value = false
    await authStore.getUserData()
    syncUserLocationFromProfile()
  } catch (error) {
    logger.error('Error updating PayPal email', error)
    alert(
      error.response?.data?.error ||
        'Failed to update PayPal email. Please try again.'
    )
  } finally {
    isUpdating.value = false
  }
}

const saveIdentityVerification = async () => {
  validateIdv()
  if (!idvValid.value || !authStore.user?.id) {
    alert(
      'Please provide valid identity verification details and ensure you are logged in.'
    )
    return
  }
  isUpdating.value = true
  try {
    const fd = new FormData()
    fd.append('document_type', idvForm.value.document_type)
    fd.append('identity_document', idDocFile.value)

    mergeUser({ document_type: idvForm.value.document_type })

    const res = await axios.put(
      `${api_base_url}/api/accounts/users/${authStore.user.id}/`,
      fd,
      {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    )
    mergeUser({
      document_type: res.data.document_type ?? idvForm.value.document_type,
      identity_document: res.data.identity_document ?? '__provided__',
    })
    identityModalVisible.value = false
    await authStore.getUserData()
    syncUserLocationFromProfile()
  } catch (error) {
    logger.error('Error saving identity verification', error)
    alert(
      error.response?.data?.error ||
        'Failed to save identity verification. Please try again.'
    )
  } finally {
    isUpdating.value = false
  }
}

const saveProofOfAddress = async () => {
  validateProof()
  if (!proofValid.value || !authStore.user?.id) {
    alert(
      'Please provide a valid proof of address file and ensure you are logged in.'
    )
    return
  }
  isUpdating.value = true
  try {
    mergeUser({ proof_of_address: '__provided__' })
    const fd = new FormData()
    fd.append('proof_of_address', proofFile.value)

    const res = await axios.put(
      `${api_base_url}/api/accounts/users/${authStore.user.id}/`,
      fd,
      {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    )
    mergeUser({
      proof_of_address: res.data.proof_of_address || '__provided__',
    })
    proofModalVisible.value = false
    await authStore.getUserData()
    syncUserLocationFromProfile()
  } catch (error) {
    logger.error('Error uploading proof of address', error)
    alert(
      error.response?.data?.error ||
        'Failed to upload proof of address. Please try again.'
    )
  } finally {
    isUpdating.value = false
  }
}
const updateAddress = async () => {
  validateAddress()
  if (!addressValid.value || !authStore.user?.id) {
    alert('Please provide valid address details and ensure you are logged in.')
    return
  }

  isUpdating.value = true

  try {
    // 1. Payload from form (only the fields the backend cares about)
    const payload = {
      street_address: addressForm.value.street_address || '',
      street_address2: addressForm.value.street_address2 || '',
      city: addressForm.value.city || '',
      state: addressForm.value.state || '',
      zip_code: addressForm.value.zip_code || '',
      country: addressForm.value.country || '',
    }

    // 2. Call the new upsert endpoint:
    //    - If user has no address → it will CREATE one
    //    - If user already has an address → it will UPDATE it
    const res = await axios.post(
      `${api_base_url}/api/locations/addresses/user-address/`,
      payload,
      { withCredentials: true }
    )

    // 3. Merge into authStore user
    mergeUser({ user_address: res.data })

    // 4. Sync location store from backend response (for map + distance)
    locationStore.userAddress = {
      street: res.data.street_address || '',
      line2: res.data.street_address2 || '',
      city: res.data.city || '',
      state: res.data.state || '',
      zipCode: res.data.zip_code || '',
      country: res.data.country || '',
    }

    if (res.data.latitude && res.data.longitude) {
      locationStore.userCoords = {
        lat: Number(res.data.latitude),
        lng: Number(res.data.longitude),
      }
    }

    // 5. Refresh full user profile (in case backend adds more)
    await authStore.getUserData()
    syncUserLocationFromProfile()

    addressModalVisible.value = false
  } catch (error) {
    logger.error('Error updating address', error)
    alert(
      error.response?.data?.error ||
        'Failed to update address. Please try again.'
    )
  } finally {
    isUpdating.value = false
  }
}

</script>

<style scoped>
/* Inputs */
.input,
select {
  @apply w-full rounded-lg border border-theme-input-border bg-theme-input px-3 py-2 text-sm text-theme-input-text
         shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-60 focus:border-primary-500;
}

/* Slide-up animation for modals */
.animate-slide-up {
  animation: slide-up 0.3s ease-out;
}
@keyframes slide-up {
  0% {
    transform: translateY(20px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Smooth radial progress transition */
svg path {
  transition: stroke-dasharray 0.5s ease;
}

/* vue-tel-input in phone modal */
:deep(.tel-input-account) {
  border: 1px solid var(--color-inputBorder) !important;
  border-radius: 0.5rem !important;
  background: var(--color-inputBg) !important;
  box-shadow: none !important;
  min-height: 42px;
}
:deep(.tel-input-account):focus-within {
  border-color: var(--color-primary, #ff6f00) !important;
  box-shadow: 0 0 0 2px rgba(255,111,0,0.15) !important;
}
:deep(.tel-input-account .vti__dropdown) {
  background: transparent !important;
  border-right: 1px solid var(--color-inputBorder);
  border-radius: 0.5rem 0 0 0.5rem;
  padding: 0 8px;
}
:deep(.tel-input-account .vti__dropdown:hover) {
  background: var(--color-bgSecondary) !important;
}
:deep(.tel-input-account input) {
  background: transparent !important;
  color: var(--color-inputText) !important;
  font-size: 0.875rem !important;
  padding: 10px 12px !important;
  border: none !important;
  outline: none !important;
}
:deep(.tel-input-account input::placeholder) { color: var(--color-muted, #9ca3af) !important; }
:deep(.tel-input-account .vti__dropdown-list) {
  background: var(--color-cardBg) !important;
  border: 1px solid var(--color-border) !important;
  border-radius: 0.5rem !important;
  box-shadow: 0 8px 20px rgba(0,0,0,0.12) !important;
  z-index: 99999 !important;
}
:deep(.tel-input-account .vti__dropdown-item) {
  padding: 8px 12px !important;
  font-size: 0.8rem !important;
  color: var(--color-inputText) !important;
}
:deep(.tel-input-account .vti__dropdown-item:hover),
:deep(.tel-input-account .vti__dropdown-item.highlighted) {
  background: var(--color-bgSecondary) !important;
}
:deep(.tel-input-account .vti__search_box) {
  border: 1px solid var(--color-inputBorder) !important;
  border-radius: 0.375rem !important;
  padding: 6px 10px !important;
  margin: 6px !important;
  width: calc(100% - 12px) !important;
  font-size: 0.8rem !important;
  background: var(--color-inputBg) !important;
  color: var(--color-inputText) !important;
}
:deep(.tel-input-account .vti__country-code) {
  color: var(--color-inputText) !important;
  font-size: 0.875rem !important;
}
</style>
